/**
 * Web: while a client's call page is open, it keeps a mark in localStorage
 * (vrs_room_tab), so the web Home in another tab of the same browser knows
 * that the call page is live.
 *
 * Home steps aside when this page signs in on the same device
 * (session_superseded), and it must not take the session back while the call
 * page is live: that ends the call. Before this mark, Home could not tell when
 * the call page was gone. It stayed on "Live call moved to another tab" with
 * no Rejoin row (Devin's three-way call, 2026-10-13). client-profile.js reads
 * the mark (roomTabAlive) and treats one older than its stale limit as gone.
 *
 * No runtime imports, so the interpreter-queue test harnesses stay as they are.
 */
export const ROOM_TAB_KEY = 'vrs_room_tab';
/** How often the mark is renewed. Home's stale limit is much longer (hidden tabs run timers late). */
export const ROOM_TAB_BEAT_MS = 10000;
let beat;
let markedRoom;
/**
 * The browser's localStorage, or undefined (native, or storage blocked).
 *
 * @returns {Storage|undefined}
 */
function browserStorage() {
    try {
        // The native window type has no localStorage; on native it is undefined.
        return globalThis.localStorage;
    }
    catch {
        return undefined;
    }
}
/**
 * The default timers and storage.
 *
 * @returns {IRoomTabDeps}
 */
function defaultDeps() {
    return {
        clearInterval: timer => clearInterval(timer),
        now: () => Date.now(),
        setInterval: (work, ms) => setInterval(work, ms),
        storage: browserStorage()
    };
}
/**
 * Writes the mark now and renews it every ROOM_TAB_BEAT_MS until stopped.
 *
 * @param {IRoomTabMark} mark - This page's call.
 * @param {IRoomTabDeps} [deps] - Timers and storage (tests).
 * @returns {void}
 */
export function startRoomTabPresence(mark, deps = defaultDeps()) {
    const store = deps.storage;
    if (!store || !mark.roomName) {
        return;
    }
    stopRoomTabPresence(false, deps);
    markedRoom = mark.roomName;
    const write = () => {
        try {
            store.setItem(ROOM_TAB_KEY, JSON.stringify({ ...mark, at: deps.now() }));
        }
        catch { /* storage full or blocked: Home then treats the page as gone */ }
    };
    write();
    beat = deps.setInterval(write, ROOM_TAB_BEAT_MS);
}
/**
 * Stops renewing the mark and, by default, removes it when it is this page's.
 * Home sees the removal at once (storage event).
 *
 * @param {boolean} [clear] - Remove the mark too.
 * @param {IRoomTabDeps} [deps] - Timers and storage (tests).
 * @returns {void}
 */
export function stopRoomTabPresence(clear = true, deps = defaultDeps()) {
    if (beat) {
        deps.clearInterval(beat);
        beat = undefined;
    }
    if (!clear || !markedRoom) {
        return;
    }
    const room = markedRoom;
    markedRoom = undefined;
    try {
        const current = JSON.parse(deps.storage?.getItem(ROOM_TAB_KEY) || 'null');
        // Another call page in this browser may have written it since.
        if (!current || current.roomName === room) {
            deps.storage?.removeItem(ROOM_TAB_KEY);
        }
    }
    catch {
        deps.storage?.removeItem(ROOM_TAB_KEY);
    }
}
